import {useState, useEffect, useCallback} from "react";
import {API_URL} from "../../config";
import styles from "./admin.module.css";
import HomeIcon from '@mui/icons-material/Home';

async function fetchBannerData() {
    const res = await fetch(API_URL, {credentials: "same-origin"});
    if (!res.ok) throw new Error("Fehler beim Laden");
    return res.json();
}

async function saveBannerData(data) {
    const res = await fetch(API_URL, {
        method: "POST",
        credentials: "same-origin",
        headers: {"Content-Type": "application/json"},
        body: JSON.stringify(data),
    });
    if (!res.ok) throw new Error("Fehler beim Speichern");
    return res.json();
}

async function login(password) {
    const res = await fetch(API_URL, {
        method: "POST",
        credentials: "same-origin",
        headers: {"Content-Type": "application/json"},
        body: JSON.stringify({action: "login", password}),
    });
    if (!res.ok) throw new Error("Falsches Passwort");
    return res.json();
}

async function logout() {
    await fetch(API_URL, {
        method: "POST",
        credentials: "same-origin",
        headers: {"Content-Type": "application/json"},
        body: JSON.stringify({action: "logout"}),
    });
}

function LoginForm({onSuccess}) {
    const [pw, setPw] = useState("");
    const [error, setError] = useState(false);
    const [loading, setLoading] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            await login(pw);
            onSuccess();
        } catch {
            setError(true);
            setPw("");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className={styles.adminBody}>
            {error && (
                <div className={styles.loginError}>Falsches Passwort. Bitte erneut versuchen.</div>
            )}
            <label className={styles.loginLabel}>Passwort</label>
            <input
                className={styles.loginInput}
                type="password"
                value={pw}
                onChange={(e) => {
                    setPw(e.target.value);
                    setError(false);
                }}
                placeholder="••••••••"
                autoFocus
            />
            <button className={styles.loginBtn} onClick={handleSubmit} disabled={loading}>
                {loading ? "Wird geprüft…" : "Anmelden"}
            </button>
        </div>
    );
}

function AdminPanel({onLogout}) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [message, setMessage] = useState(null);

    useEffect(() => {
        fetchBannerData()
            .then(setData)
            .catch(() => setMessage({type: "error", text: "Fehler beim Laden der Daten."}))
            .finally(() => setLoading(false));
    }, []);

    const handleToggle = useCallback(() => {
        setData((prev) => ({...prev, bannerActive: !prev.bannerActive}));
        setMessage(null);
    }, []);

    const handleField = useCallback((field, value) => {
        setData((prev) => ({...prev, [field]: value}));
        setMessage(null);
    }, []);

    const handleSave = async () => {
        setSaving(true);
        setMessage(null);
        try {
            await saveBannerData(data);
            setMessage({type: "success", text: "Gespeichert Banner-Status wurde aktualisiert."});
        } catch {
            setMessage({type: "error", text: "Fehler beim Speichern. Bitte erneut versuchen."});
        } finally {
            setSaving(false);
        }
    };

    const handleLogout = async () => {
        await logout();
        onLogout();
    };

    if (loading) return <div className={styles.loadingState}>Lade Banner-Daten…</div>;

    if (!data) {
        return (
            <div className={styles.adminBody}>
                {message && <div className={styles.msgError}>{message.text}</div>}
            </div>
        );
    }

    return (
        <div className={styles.adminBody}>
            <div className={styles.statusRow}>
                <div>
                    <div className={styles.statusLabel}>Banner</div>
                    <div className={styles.statusDesc}>
                        {data.bannerActive ? "Wird auf der Webseite angezeigt" : "Nicht sichtbar für Besucher"}
                    </div>
                </div>
                <button
                    className={`${styles.toggleSwitch} ${data.bannerActive ? styles.toggleSwitchOn : ""}`}
                    onClick={handleToggle}
                    aria-label="Banner umschalten"
                />
            </div>

            <hr className={styles.divider}/>

            <div className={styles.fieldGroup}>
                <label className={styles.fieldLabel}>Titel</label>
                <input className={styles.fieldInput} value={data.title || ""}
                       onChange={(e) => handleField("title", e.target.value)}
                       placeholder="z.B. Praxis vorübergehend geschlossen"/>
            </div>
            <div className={styles.fieldGroup}>
                <label className={styles.fieldLabel}>Text</label>
                <textarea className={styles.fieldInput} value={data.text || ""}
                          onChange={(e) => handleField("text", e.target.value)}
                          placeholder="Informationstext für Patienten…"/>
            </div>
            <div className={styles.fieldGroup}>
                <label className={styles.fieldLabel}>Von</label>
                <input className={styles.fieldInput} value={data.from || ""}
                       onChange={(e) => handleField("from", e.target.value)} placeholder="z.B. 28. Juli 2025"/>
            </div>
            <div className={styles.fieldGroup}>
                <label className={styles.fieldLabel}>Bis</label>
                <input className={styles.fieldInput} value={data.to || ""}
                       onChange={(e) => handleField("to", e.target.value)} placeholder="z.B. 15. August 2025"/>
            </div>
            <div className={styles.fieldGroup}>
                <label className={styles.fieldLabel}>Wiedereröffnung</label>
                <input className={styles.fieldInput} value={data.returnDate || ""}
                       onChange={(e) => handleField("returnDate", e.target.value)} placeholder="z.B. 18. August 2025"/>
            </div>
            <div className={styles.fieldGroup}>
                <label className={styles.fieldLabel}>Notfall-Hinweis</label>
                <input className={styles.fieldInput} value={data.emergency || ""}
                       onChange={(e) => handleField("emergency", e.target.value)}
                       placeholder="z.B. In dringenden Fällen: 116 117"/>
            </div>

            <button className={styles.saveBtn} onClick={handleSave} disabled={saving}>
                {saving ? "Wird gespeichert…" : "Speichern & Veröffentlichen"}
            </button>

            {message && (
                <div className={message.type === "success" ? styles.msgSuccess : styles.msgError}>
                    {message.text}
                </div>
            )}

            <button className={styles.logoutBtn} onClick={handleLogout}>
                Abmelden
            </button>
        </div>
    );
}

export default function Admin() {
    const [authenticated, setAuthenticated] = useState(false);

    return (
        <div className={styles.adminPage}>
            <div className={styles.adminCard}>
                <div className={styles.adminHeader}>
                    <div>
                        <h1>Praxis-Verwaltung</h1>
                        <p>Banner Steuerung · Arztpraxis Wagner</p>
                    </div>
                    <div className={styles.homeButton}>
                        <button onClick={() => window.location.href = "/"}>
                            <HomeIcon/>
                        </button>
                    </div>
                </div>
                {authenticated ? (
                    <AdminPanel onLogout={() => setAuthenticated(false)}/>
                ) : (
                    <LoginForm onSuccess={() => setAuthenticated(true)}/>
                )}
            </div>
        </div>
    );
}
